import { useState, useEffect } from 'react';
import { adminApiService } from '../../services/api';

export interface AgencyEmail {
  id: number;
  email: string;
  createdAt: string;
  createdBy: string;
}

export interface AgencyState {
  isAgency: boolean;
  authorizedEmails: AgencyEmail[];
}

export function useAgencyData(reporterId: string) {
  const [agency, setAgency] = useState<AgencyState>({ isAgency: false, authorizedEmails: [] });
  const [agencyLoading, setAgencyLoading] = useState(true);
  const [agencyError, setAgencyError] = useState<string | null>(null);
  const [addEmail, setAddEmail] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addWarning, setAddWarning] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const loadAgency = async () => {
    try {
      setAgencyLoading(true);
      setAgencyError(null);
      const res = await adminApiService.getAgencyStatus(reporterId);
      if (res.success && res.data) {
        setAgency({
          isAgency: res.data.isAgency,
          authorizedEmails: res.data.authorizedEmails ?? [],
        });
      }
    } catch {
      setAgencyError('Failed to load agency status');
    } finally {
      setAgencyLoading(false);
    }
  };

  useEffect(() => {
    const fetchAgency = async () => {
      try {
        setAgencyLoading(true);
        setAgencyError(null);
        const res = await adminApiService.getAgencyStatus(reporterId);
        if (res.success && res.data) {
          setAgency({
            isAgency: res.data.isAgency,
            authorizedEmails: res.data.authorizedEmails ?? [],
          });
        }
      } catch {
        setAgencyError('Failed to load agency status');
      } finally {
        setAgencyLoading(false);
      }
    };
    fetchAgency();
  }, [reporterId]);

  const handleToggleAgency = async () => {
    try {
      if (agency.isAgency) {
        await adminApiService.removeAgencyFlag(reporterId);
        await loadAgency();
        setSnackbar('Agency flag removed');
      } else {
        await adminApiService.setAgencyFlag(reporterId);
        await loadAgency();
        setSnackbar('Reporter flagged as agency');
      }
    } catch {
      setSnackbar('Failed to update agency flag');
    }
  };

  const handleAddEmail = async () => {
    setAddError(null);
    setAddWarning(null);
    setAddLoading(true);
    try {
      const res = await adminApiService.addAgencyEmail(reporterId, addEmail);
      if (res.success) {
        setAddEmail('');
        if (res.data?.warning) setAddWarning(res.data.warning);
        await loadAgency();
        setSnackbar('Email added');
      }
    } catch (err: any) {
      setAddError(err?.response?.data?.error ?? 'Failed to add email');
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemoveEmail = async (email: string) => {
    try {
      await adminApiService.removeAgencyEmail(reporterId, email);
      await loadAgency();
      setSnackbar('Email removed');
    } catch {
      setSnackbar('Failed to remove email');
    }
  };

  return {
    agency, agencyLoading, agencyError,
    addEmail, setAddEmail, addLoading, addWarning, addError,
    handleToggleAgency, handleAddEmail, handleRemoveEmail,
    snackbar, setSnackbar,
  };
}
